"use client";
import Script from "next/script";
import { useEffect } from "react";
import { buildGaPageViewParams } from "@pulsex/utils";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __alakhbarGaSent?: Record<string, true>;
  }
}

function sendGaPageView(
  measurementId: string,
  params: Record<string, string>
): boolean {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }

  const path = window.location.pathname;
  const sentKey = `${path}:${JSON.stringify(params)}`;
  window.__alakhbarGaSent = window.__alakhbarGaSent || {};
  if (window.__alakhbarGaSent[sentKey]) {
    return true;
  }

  window.gtag("event", "page_view", {
    send_to: measurementId,
    page_path: path,
    ...params,
  });
  window.__alakhbarGaSent[sentKey] = true;
  return true;
}

export const GoogleAnalytics = ({
  googleAnalyticsId,
  nonce,
  pageData,
}: {
  googleAnalyticsId: string;
  nonce?: any;
  pageData?: any;
}) => {
  const eventParams = buildGaPageViewParams(pageData);
  const serializedParams = JSON.stringify(eventParams).replace(/</g, "\\u003c");
  const measurementIdLiteral = JSON.stringify(googleAnalyticsId);

  useEffect(() => {
    const params = buildGaPageViewParams(pageData);
    if (sendGaPageView(googleAnalyticsId, params)) return;

    let timeoutId = 0;
    let tries = 0;
    const poll = () => {
      if (sendGaPageView(googleAnalyticsId, params)) return;
      tries += 1;
      if (tries < 100) {
        timeoutId = window.setTimeout(poll, 100);
      }
    };
    timeoutId = window.setTimeout(poll, 0);

    return () => window.clearTimeout(timeoutId);
  }, [pageData, googleAnalyticsId]);

  return (
    <>
      <Script
        id="gtag-js"
        strategy="lazyOnload"
        nonce={nonce}
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
      />
      <Script id="gtag-init" strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', ${measurementIdLiteral}, {
            send_page_view: false
          });
          try {
            var measurementId = ${measurementIdLiteral};
            var params = ${serializedParams};
            var path = window.location.pathname;
            var sentKey = path + ':' + JSON.stringify(params);
            window.__alakhbarGaSent = window.__alakhbarGaSent || {};
            if (!window.__alakhbarGaSent[sentKey] && typeof gtag === 'function') {
              gtag('event', 'page_view', Object.assign({
                send_to: measurementId,
                page_path: path
              }, params));
              window.__alakhbarGaSent[sentKey] = true;
            }
          } catch (error) {}
        `}
      </Script>
    </>
  );
};
